import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Logo from '../Logo/Logo';
import './MagicLoader.css';
import { FeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagsContext';

interface MagicLoaderProps {
    isExiting?: boolean;
}

const TIP_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20'] as const;
const LAIKA_TIP_KEY = '2';

const MagicLoader: React.FC<MagicLoaderProps> = ({ isExiting = false }) => {
    const { t } = useTranslation('common');
    const { isFeatureEnabled } = useFeatureFlags();

    const tipsPool = useMemo(() => {
        const keys = isFeatureEnabled(FeatureFlag.LAIKA)
            ? TIP_KEYS
            : TIP_KEYS.filter((key) => key !== LAIKA_TIP_KEY);
        return keys.map((key) => t(`magicLoader.tips.${key}`));
    }, [isFeatureEnabled, t]);

    const [currentTip, setCurrentTip] = useState(() => {
        const pool = tipsPool.length ? tipsPool : [t('magicLoader.tips.1')];
        return pool[Math.floor(Math.random() * pool.length)];
    });

    useEffect(() => {
        const pool = tipsPool.length ? tipsPool : [t('magicLoader.tips.1')];
        setCurrentTip(pool[Math.floor(Math.random() * pool.length)]);
    }, [tipsPool, t]);

    const [progress, setProgress] = React.useState(0);

    React.useEffect(() => {
        const timer = setInterval(() => {
            setProgress(prev => {
                if (prev >= 99) return 99;
                return prev + Math.floor(Math.random() * 12);
            });
        }, 150);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className={`magic-loader-container ${isExiting ? 'exit' : ''}`}>
            <div className="mesh-bg">
                <div className="mesh-blob blob-1" />
                <div className="mesh-blob blob-2" />
                <div className="mesh-blob blob-3" />
            </div>

            <div className="glass-loader-card">
                <div className="loading-logo-reveal">
                    <Logo fontSize="4rem" className="magic-loader-logo" />
                    <span className="magic-loader-progress-label">
                        {isExiting ? '100%' : `${progress}%`}
                    </span>
                </div>

                <div className="loader-status-bar">
                    <div className="loader-status-progress" style={{ width: isExiting ? '100%' : `${progress}%` }} />
                </div>

                <div className="loader-info-group">
                    <div className="loader-subtitle">
                        {t('magicLoader.loadingProfile')}
                    </div>

                    <div className="loader-tip-box">
                        <span className="tip-title">{t('magicLoader.didYouKnow')}</span>
                        <p className="tip-content">{currentTip}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MagicLoader;
